/**
 * redactionThinkingStore — le reasoning LLM streamé pendant l'insertion canvas,
 * **un par travail**.
 *
 * Le SSE act-generation (`/stream-plan-detaille`, `/stream`) émet des frames
 * `{type:'thinking', content}` (reasoning-delta de la façade lib/llm, cf.
 * `apps/backend/routes/streaming/chatEventsToSse.ts:113`). L'écoute qui les lit
 * vit hors de React (`redactionStreamHub`), mais le composant qui doit les
 * AFFICHER (`RedactionPlanProgress`) vit dans la frise du chat. Ce store zustand
 * est le pont entre les deux, à l'image de `redactionJobStore` pour les jobIds.
 *
 * ## Indexé par TRAVAIL (#1152)
 *
 * Il a longtemps tenu **un seul** raisonnement, sur la foi d'un invariant qui
 * n'a jamais existé : « un seul job canvas streame à la fois ». Le hub, lui,
 * écoute autant de travaux qu'il en est lancé, et une conversation peut même en
 * porter deux (le plan, puis l'acte lancé dans la foulée). Le raisonnement du
 * second écrasait donc celui du premier, et la carte de progression de la
 * première conversation devenait muette.
 *
 * La clé est le **travail**, pas la conversation : c'est par le jobId que la
 * carte de progression se reconnaît, et deux travaux d'une même conversation
 * doivent garder chacun le sien. La conversation est retenue dans l'entrée pour
 * qu'un changement de compte puisse oublier ce qui lui appartient.
 *
 * **Son producteur est `useRedactionStreamingState`** (l'affichage du canvas),
 * qui ne suit que le travail affiché : les autres travaux n'écrivent rien ici.
 * Le relais hors React qui devait refléter toutes les écoutes (#1152) n'a
 * jamais été importé après #1044 ; il a été supprimé plutôt que rebranché (#1329).
 */

import { create } from 'zustand';

/** Le raisonnement d'un travail, tel que la carte de progression le lit. */
export interface RaisonnementDUnTravail {
  /** Reasoning accumulé par l'écoute, reflété tel quel. */
  content: string;
  /**
   * Passe à true au premier token de CONTENU (pas de reasoning) : le modèle a
   * fini de réfléchir — le shimmer s'arrête et le bloc se replie (parité
   * add-in Word : `thinkingContent.length > 0 && characterCount === 0`).
   */
  hasContentStarted: boolean;
}

interface RedactionThinkingState {
  /** Une entrée par travail écouté. */
  parJob: Record<string, RaisonnementDUnTravail>;
  /**
   * Reflète l'état du raisonnement tel que l'écoute le tient (#857).
   *
   * Un **reflet**, pas une accumulation : depuis que le flux est lu hors de
   * React (`redactionStreamHub`), la seule mémoire des deltas est l'écoute
   * elle-même. Accumuler ici perdrait tout ce qui arrive entre deux rendus —
   * et le reflet, lui, est idempotent : le rejouer ne double rien.
   */
  refleter: (jobId: string, content: string, hasContentStarted: boolean) => void;
  /** Oublie un travail (son écoute a disparu). */
  oublierLeTravail: (jobId: string) => void;
  /** Tout oublier — changement de compte, et tests. */
  reinitialiserTout: () => void;
}

export const useRedactionThinkingStore = create<RedactionThinkingState>((set) => ({
  parJob: {},
  refleter: (jobId, content, hasContentStarted) =>
    set((state) => {
      const precedent = state.parJob[jobId];
      // Le hub réveille ses abonnés à CHAQUE bloc : sans cette égalité, le
      // reflet poserait un état neuf au rythme du flux et re-rendrait la carte
      // de progression des dizaines de fois par seconde pour rien.
      if (
        precedent
        && precedent.content === content
        && precedent.hasContentStarted === hasContentStarted
      ) {
        return state;
      }
      return {
        parJob: {
          ...state.parJob,
          [jobId]: { content, hasContentStarted },
        },
      };
    }),
  oublierLeTravail: (jobId) =>
    set((state) => {
      if (!(jobId in state.parJob)) return state;
      const { [jobId]: _oublie, ...reste } = state.parJob;
      return { parJob: reste };
    }),
  reinitialiserTout: () => set({ parJob: {} }),
}));
